/**
 * @fileoverview Mini-player showing a completed/failed browser agent run summary.
 *
 * Reuses StatusPill from the BrowserAgentViewer module.
 * Clicking the bar expands the full BrowserAgentViewer inline below.
 */
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';

import { BrowserAgentViewer, StatusPill } from '@/components/browser-use-scraper';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { useGetScraperRunByWorkflowIdQuery } from '@/services/api/browserUseScraperService';

const MIN_HEIGHT = 200;
const MAX_HEIGHT_VH = 0.85;
const DEFAULT_HEIGHT = Math.round(window.innerHeight * 0.6);

interface BrowserAgentMiniPlayerProps {
  workflowId: string;
  snapToUrl?: string | null;
  /** Override default expanded height in pixels */
  defaultHeight?: number;
}

export const BrowserAgentMiniPlayer: React.FC<BrowserAgentMiniPlayerProps> = ({
  workflowId,
  snapToUrl,
  defaultHeight,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [viewerHeight, setViewerHeight] = useState(defaultHeight ?? DEFAULT_HEIGHT);
  const [showPopout, setShowPopout] = useState(false);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startHeight: viewerHeight };

    const onDragMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      // Dragging up increases height (mouse Y decreases)
      const delta = dragRef.current.startY - ev.clientY;
      const maxHeight = Math.round(window.innerHeight * MAX_HEIGHT_VH);
      const newHeight = Math.min(maxHeight, Math.max(MIN_HEIGHT, dragRef.current.startHeight + delta));
      setViewerHeight(newHeight);
    };

    const onDragEnd = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
    };

    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
  }, [viewerHeight]);
  const { data: run } = useGetScraperRunByWorkflowIdQuery(workflowId, {
    skip: !workflowId,
  });

  if (!run) return null;

  const lastStep = run.steps?.[run.steps.length - 1];
  const screenshotUrl = lastStep?.screenshot_url;

  return (
    <div className="border-t border-zinc-200 dark:border-zinc-700">
      {/* Toggle bar */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
      >
        {/* Thumbnail */}
        <div className="h-10 w-16 flex-shrink-0 overflow-hidden rounded-md border bg-muted">
          {screenshotUrl ? (
            <img
              src={screenshotUrl}
              alt="Last step"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-foreground truncate">
              Browser Agent
            </span>
            <StatusPill status={run.status} successful={run.successful} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {run.steps?.length ?? 0} steps
            {run.usage ? ` · $${run.usage.totalCost.toFixed(3)}` : ''}
          </p>
        </div>

        {/* Expand/collapse icon */}
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        )}
      </button>

      {/* Inline expanded viewer with resize handle */}
      {expanded && (
        <div className="relative border-t border-zinc-200 dark:border-zinc-700">
          {/* Resize handle (drag upward to grow) */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            onMouseDown={onDragStart}
            className="absolute top-0 left-0 right-0 z-10 flex h-2 cursor-ns-resize items-center justify-center hover:bg-zinc-200/50 dark:hover:bg-zinc-700/50"
          >
            <div className="h-0.5 w-8 rounded-full bg-zinc-300 dark:bg-zinc-600" />
          </div>

          <div style={{ height: viewerHeight }} className="overflow-hidden">
            <BrowserAgentViewer workflowId={workflowId} compact hideHeader snapToUrl={snapToUrl} onPopOut={() => setShowPopout(true)} />
          </div>
        </div>
      )}

      {/* Pop-out dialog */}
      <Dialog open={showPopout} onOpenChange={setShowPopout}>
        <DialogContent className="max-w-[95vw] w-full h-[90vh] p-0 overflow-hidden">
          <BrowserAgentViewer workflowId={workflowId} snapToUrl={snapToUrl} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
